import {
  Checkbox,
  DefaultButton,
  DialogFooter,
  mergeStyleSets,
} from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import GroupPolicyNumberField from "components/controls/GroupPolicyNumberField";
import { useGetFleetInsurerDefaultSettings } from "hooks/data/queries/getFleetInsurerDefaultSettings";
import { EActionDialog } from "pages/fleet-form/FleetForm/constants";
import VehicleTypeDependencyField from "pages/fleet-form/actionDialogs/VehicleTypeDependencyField";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "store/hooks";

const AnnualGrossPremiumFormFields = ({
  settingId,
  fieldProps,
  defaultData,
  insurerOptions,
  carUsageOptions,
  vehicleTypeCodeOptions,
  isLoading,
  premiumTypeOptions,
  isTradeLicensePlate,
  setTradeLicensePlate,
  listPriceTypeOptions,
}) => {
  const classes = getClassNames();
  const { setFieldValue, values, errors, handleChange } = fieldProps;
  const { t } = useTranslation();
  const { fleetId } = useParams() as any;

  const isReadOnly =
    useSelector((s) => s.fleetForm.dialog.type) === EActionDialog.ReadOnly;
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);

  // Replace old selectors with new fleet insurer default settings
  const { data: fleetInsurerDefaultSettings } =
    useGetFleetInsurerDefaultSettings(fleetId);

  // Effect to clear list price values when premium type changes away from HULL types
  useEffect(() => {
    if (
      values.premiumType?.code !== "HULL_BASIC" &&
      values.premiumType?.code !== "HULL"
    ) {
      setFieldValue("listPriceType", null);
      setFieldValue("listPriceMin", "");
      setFieldValue("listPriceMax", "");
    }
  }, [values.premiumType?.code, setFieldValue]);

  // Create dynamic group policy options based on selected insurer only
  const getInsurerGroupPolicyNumberOptions = useCallback(
    (selectedInsurerValue: number | null) => {
      if (!selectedInsurerValue || !fleetInsurerDefaultSettings) {
        return [];
      }

      const retVal = new Set<any>();

      fleetInsurerDefaultSettings
        .filter(
          (setting) =>
            setting.insurerPartnerNumber === selectedInsurerValue &&
            setting.insurerGroupPolicyNumber !== null &&
            setting.insurerGroupPolicyNumber !== ""
        )
        .map((setting) => ({
          label: setting.insurerGroupPolicyNumber,
          value: setting.insurerGroupPolicyNumber,
        }))
        .forEach((item) => {
          if (!Array.from(retVal).find((it) => it.value === item.value)) {
            retVal.add(item);
          }
        });

      return Array.from(retVal);
    },
    [fleetInsurerDefaultSettings]
  );

  // Replace old useMemo with new dynamic approach - no premium type dependency
  const insurerGroupPolicyNumberOptions = useMemo(() => {
    const selectedInsurerValue = values?.insurerPartnerNumber?.value || null;

    return getInsurerGroupPolicyNumberOptions(selectedInsurerValue);
  }, [values?.insurerPartnerNumber, getInsurerGroupPolicyNumberOptions]);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <Checkbox
            id="isTradeLicensePlate"
            label={t("bfm.tradeLicensePlates.label")}
            styles={{
              root: {
                fontWeight: 600,
              },
            }}
            disabled={isReadOnly}
            checked={isTradeLicensePlate}
            onChange={(ev) => {
              setFieldValue("vehicleType", null);
              setFieldValue("carUsage", null);
              setTradeLicensePlate(!isTradeLicensePlate);
            }}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.validFromDate}
            name={"validFromDate"}
            disabled={false}
            label={"bfm.vehicleForm.validFromDate.label"}
            placeholder={t("bfm.selectDate")}
            required
            confidentiality={null}
          />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          className={classes.fieldItem}
          style={
            fleetAppSettings?.INCLUDE_GROUP_POLICY
              ? { marginRight: "10px" }
              : null
          }
        >
          <DoubleNumberInputField
            id="attributeValue"
            setFieldValue={setFieldValue}
            value={values.attributeValue}
            onChange={handleChange}
            errors={errors}
            required
            name={"attributeValue"}
            disabled={isReadOnly}
            label={"bfm.fleet.setting.attributeValue.label"}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <ComboBoxField
            id="premiumType"
            disabled={isReadOnly}
            errors={errors}
            name="premiumType"
            required
            label={"bfm.fleet.premiumType.label"}
            setFieldValue={setFieldValue}
            options={premiumTypeOptions || []}
            defaultValue={values?.premiumType}
            confidentiality={null}
          />
        </div>
      </div>
      <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
        <ComboBoxField
          id="insurerPartnerNumber"
          disabled={isReadOnly}
          errors={errors}
          required
          name="insurerPartnerNumber"
          label={"bfm.insurer.label"}
          setFieldValue={setFieldValue}
          options={insurerOptions || []}
          defaultValue={values?.insurerPartnerNumber}
          confidentiality={null}
          onChange={(selectedOption) => {
            // When insurer changes, clear the group policy number
            setFieldValue("insurerPartnerNumber", selectedOption);
            setFieldValue("insurerGroupPolicyNumber", null);
          }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        {fleetAppSettings?.INCLUDE_GROUP_POLICY ? (
          <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
            <GroupPolicyNumberField
              id="insurerGroupPolicyNumber"
              setFieldValue={setFieldValue}
              options={insurerGroupPolicyNumberOptions || []}
              defaultValue={values?.insurerGroupPolicyNumber}
              errors={errors}
              name={"insurerGroupPolicyNumber"}
              disabled={isReadOnly}
              label={"bfm.fleet.setting.insurerGroupPolicyNumber.label"}
              confidentiality={null}
              isClearable={false}
            />
          </div>
        ) : (
          <div
            className={classes.fieldItem}
            style={{ marginRight: "10px" }}
          ></div>
        )}
      </div>
      {values.premiumType?.code === "HULL_BASIC" ||
      values.premiumType?.code === "HULL" ? (
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
            <>
              <ComboBoxField
                id="listPriceType"
                disabled={isReadOnly}
                errors={errors}
                name="listPriceType"
                required
                label={"bfm.vehicleForm.listPriceTypeCode.label"}
                setFieldValue={setFieldValue}
                options={listPriceTypeOptions || []}
                defaultValue={values?.listPriceType}
                confidentiality={null}
              />
              <div
                className={classes.fieldItem}
                style={{ display: "flex", gap: "15px" }}
              >
                <div style={{ width: "50%" }}>
                  <DoubleNumberInputField
                    id="listPriceMin"
                    setFieldValue={setFieldValue}
                    value={values.listPriceMin}
                    onChange={handleChange}
                    errors={errors}
                    required={
                      values.listPriceMin !== "" || values.listPriceMax !== ""
                    }
                    name={"listPriceMin"}
                    disabled={isReadOnly}
                    label={"bfm.listPriceMin.label"}
                  />
                </div>
                <div style={{ width: "50%" }}>
                  <DoubleNumberInputField
                    id="listPriceMax"
                    setFieldValue={setFieldValue}
                    value={values.listPriceMax}
                    onChange={handleChange}
                    errors={errors}
                    required={
                      values.listPriceMin !== "" || values.listPriceMax !== ""
                    }
                    name={"listPriceMax"}
                    disabled={isReadOnly}
                    label={"bfm.listPriceMax.label"}
                  />
                </div>
              </div>
            </>
          </div>
          <div
            className={classes.fieldItem}
            style={{ marginLeft: "10px" }}
          ></div>
        </div>
      ) : null}
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          {!isTradeLicensePlate ? (
            <ComboBoxField
              id="carUsage"
              disabled={isReadOnly}
              errors={errors}
              name="carUsage"
              label={"bfm.vehicleForm.carUsageCode.label"}
              setFieldValue={setFieldValue}
              options={carUsageOptions || []}
              defaultValue={values?.carUsage}
              confidentiality={null}
              isMulti={true}
            />
          ) : null}
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          {!isTradeLicensePlate ? (
            <ComboBoxField
              id="vehicleType"
              disabled={isTradeLicensePlate || isReadOnly}
              errors={errors}
              name="vehicleType"
              required={!isTradeLicensePlate}
              label={"bfm.vehicleForm.vehicleTypeCode.label"}
              setFieldValue={setFieldValue}
              options={vehicleTypeCodeOptions || []}
              defaultValue={values?.vehicleType}
              confidentiality={null}
              isMulti={true}
            />
          ) : null}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <VehicleTypeDependencyField
          values={values}
          vehicleTypeSelected={values?.vehicleType}
          fieldItemStyle={classes.fieldItem}
          errors={errors}
          setFieldValue={setFieldValue}
          selectedDependency={defaultData}
          disabled={isReadOnly}
        />
      </div>
      <div className={classes.placeholder}></div>
      {!isReadOnly && (
        <DialogFooter className={classes.footer}>
          <DefaultButton
            id="saveButton"
            primary
            text={t("greco.save")}
            type="submit"
            iconProps={{
              iconName: "Save",
            }}
            disabled={isLoading}
          />
        </DialogFooter>
      )}
    </>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    placeholder: {
      height: 200,
    },
    fieldItem: {
      width: "100%",
      marginTop: 10,
    },
    footer: {
      marginTop: 50,
    },
  });

export default AnnualGrossPremiumFormFields;
